/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx } from '@emotion/react'
import { Fragment, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import {
  EuiAccordion,
  EuiButtonIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiLink,
  EuiPopover,
  EuiPopoverTitle,
  EuiSpacer,
  EuiText,
  useEuiTheme,
  useGeneratedHtmlId,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { Region } from '@modules/cluster-user-api/v1/types'
import { convertToClusterRegions, extractPlatformIds } from '@modules/utils/region-conversion'
import ProviderSelector from '@modules/project-creation-components/ProviderSelector'
import { useBoundProviderAndRegionState } from '@modules/project-creation-lib/hooks'
import type { NonEmptyArray } from '@modules/ts-utils'
import { useProfile } from '@modules/profile-lib/hooks'
import SettingSummary from '@modules/onboarding-combine-flow/components/SettingSummary'
import { DocLink } from '@modules/doclinks'

import ServerlessRegionSelector from './ServerlessRegionSelector'

import type { FunctionComponent } from 'react'

const CSPRegionFormRow: FunctionComponent<{
  isUserSelection: boolean
  regions: NonEmptyArray<Region>
  onChange: (region: Region) => void
}> = ({ isUserSelection, regions, onChange }) => {
  const cloudProviderHtmlId = useGeneratedHtmlId()
  const profile = useProfile()
  const regionHtmlId = useGeneratedHtmlId()
  const [isOpen, setIsOpen] = useState(false)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const { formatMessage } = useIntl()

  const {
    selectedRegion,
    selectedProvider,
    providers,
    providerRegions,
    setSelectedProvider,
    setSelectedRegion,
  } = useBoundProviderAndRegionState(regions)

  const { euiTheme } = useEuiTheme()

  const disabledProvider =
    profile?.domain === 'gcp' || profile?.domain === 'azure' || profile?.domain === 'aws'

  useEffect(() => {
    onChange(selectedRegion)
  }, [selectedRegion, onChange])

  return (
    <EuiFlexGroup direction='row' gutterSize='s'>
      {!isOpen && (
        <EuiFlexItem grow={true} css={{ width: 300 }}>
          <SettingSummary
            region={selectedRegion as any}
            availableRegions={convertToClusterRegions(providerRegions)}
            availablePlatforms={extractPlatformIds(providerRegions)}
          />
        </EuiFlexItem>
      )}
      <EuiFlexItem>
        <EuiAccordion
          id='providerAccordion'
          buttonContent={AdvancedSettingsHeaderAction({ isOpen })}
          initialIsOpen={false}
          onToggle={() => setIsOpen(!isOpen)}
          arrowDisplay='none'
        >
          <EuiFlexGroup direction='column' gutterSize='s' css={{ width: '100%' }}>
            <EuiFlexItem>
              <EuiFormRow>
                <ProviderSelector
                  value={selectedProvider}
                  availableProviders={providers}
                  comingSoon={true}
                  onChange={setSelectedProvider}
                  showPrepend={true}
                  aria-labelledby={cloudProviderHtmlId}
                  disabled={disabledProvider}
                  data-test-id='providerSelector'
                />
              </EuiFormRow>
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiFormRow>
                <ServerlessRegionSelector
                  selectedRegion={selectedRegion}
                  allowedRegions={providerRegions}
                  comingSoon={true}
                  onSelectionChange={setSelectedRegion}
                  aria-labelledby={regionHtmlId}
                  data-test-id='regionSelector'
                />
              </EuiFormRow>
            </EuiFlexItem>
          </EuiFlexGroup>
          {isUserSelection && (
            <Fragment>
              <EuiSpacer size='l' />
              <EuiText size='s'>
                <FormattedMessage
                  id='csp-region-form-row.additional-providers-and-locations-elastic-cloud-hosted'
                  defaultMessage='Not seeing your preferred provider or region?'
                />
                <EuiPopover
                  button={
                    <span css={{ marginLeft: 3 }}>
                      <EuiButtonIcon
                        iconType='question'
                        onClick={() => setIsPopoverOpen(!isPopoverOpen)}
                        aria-label={formatMessage({
                          id: 'csp-region-form-row.region-availability-info',
                          defaultMessage: 'Region availability information',
                        })}
                      />
                    </span>
                  }
                  isOpen={isPopoverOpen}
                  closePopover={() => setIsPopoverOpen(false)}
                  anchorPosition='downCenter'
                  panelPaddingSize='m'
                  panelProps={{
                    css: {
                      width: 370,
                      maxWidth: '100%',
                      backgroundColor: euiTheme.colors.backgroundBasePlain,
                      color: euiTheme.colors.textParagraph,
                      border: `1px solid ${euiTheme.colors.borderBasePlain}`,
                    },
                  }}
                >
                  <Fragment>
                    <EuiPopoverTitle paddingSize='m' css={{ fontSize: 14 }}>
                      <FormattedMessage
                        id='csp-region-form-row.edit-settings.tooltip'
                        defaultMessage='Region availability'
                      />
                    </EuiPopoverTitle>
                    <EuiText size='s' color='paragraph'>
                      <FormattedMessage
                        id='csp-region-form-row.edit-settings.tooltip2'
                        defaultMessage='Learn more about Elastic Cloud Serverless region availability in {docsLink}'
                        values={{
                          docsLink: (
                            <DocLink link='cloudServerlessRegionAvailability'>
                              <FormattedMessage
                                id='csp-region-form-row.edit-settings.docs-link'
                                defaultMessage='Docs'
                              />
                            </DocLink>
                          ),
                        }}
                      />
                    </EuiText>
                    <EuiSpacer size='s' />
                    <EuiText size='s' color='paragraph'>
                      <FormattedMessage
                        id='csp-region-form-row.edit-settings.tooltip3'
                        defaultMessage='Or "Go back" and select "Elastic Cloud Hosted", which offers additional regions, globally.'
                      />
                    </EuiText>
                  </Fragment>
                </EuiPopover>
              </EuiText>
            </Fragment>
          )}
        </EuiAccordion>
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}

const AdvancedSettingsHeaderAction: FunctionComponent<{
  isOpen: boolean
}> = ({ isOpen }) => {
  if (isOpen) {
    return null
  }

  return (
    <EuiLink>
      <FormattedMessage id='accordion-button.expand' defaultMessage='Edit settings' />
    </EuiLink>
  )
}

export default CSPRegionFormRow
